import { weaponMaxDurability } from "../combat/wear.js";
import { getItemDef } from "../items/db.js";
import type { ArmorItemDef, ItemDef, ItemInstance, WeaponItemDef } from "../items/types.js";
import { baseValueEur } from "./prices.js";

/**
 * The repair counter: what it costs to put a plate or a gun back together, and
 * what it costs the item. Shared because the client's menu PRINTS the price
 * and the server CHARGES it.
 *
 * One bench for both kinds: armour is worn by rounds that hit it, a weapon by
 * rounds that leave it (`combat/wear.ts`), and both keep `durability` and a
 * shaved `maxDurability` on the instance. A repair fills the item up and
 * shaves `REPAIR_MAX_LOSS_FRAC` of the DEF's maximum off the ceiling, which is
 * what keeps armour and guns consumable. Trader-less (no `buys` map, no
 * spread — the euros simply leave) and menu phase only.
 */

/**
 * Fraction of the DEF's maximum every repair shaves off the instance's ceiling
 * — a flat amount per repair, not a decay: 0.6 of a 40-point vest (~66 repairs
 * to nothing), 1.5 of a gun's hundred (the same 66 visits).
 */
export const REPAIR_MAX_LOSS_FRAC = 0.015;

/**
 * Cost of a FULL repair as a fraction of base value; partial repairs pro-rate.
 * Under a quarter of the cheapest replacement (≥ 1.4× base), so the counter is
 * worth walking to but never free — and the ceiling shave keeps a repaired
 * plate the cheaper deal, never the better one.
 */
export const REPAIR_COST_FRAC = 0.3;

/**
 * The ceiling THIS plate can be filled to — its own shaved one if repaired, the
 * def's otherwise. The one place `item.maxDurability ?? …` is spelled. Armour's
 * own; `weaponMaxDurability` is the twin and `repairable` picks between them.
 */
export function armorMaxDurability(def: ArmorItemDef, item: ItemInstance): number {
  return item.maxDurability ?? def.maxDurability;
}

/** Anything the bench will look at: a plate or a gun. */
type RepairableDef = ArmorItemDef | WeaponItemDef;

/**
 * The def behind `item` if the bench takes that kind of thing, else `null`. One
 * gate for both halves, so `repairCostEur` and `applyRepair` refuse under the
 * same conditions.
 */
function repairable(item: ItemInstance): RepairableDef | null {
  const def: ItemDef | null = getItemDef(item.defId);
  if (def === null) return null;
  return def.kind === "armor" || def.kind === "weapon" ? def : null;
}

/** The ceiling this instance can be filled to, whichever kind it is. */
function ceilingOf(def: RepairableDef, item: ItemInstance): number {
  return def.kind === "armor" ? armorMaxDurability(def, item) : weaponMaxDurability(def, item);
}

/**
 * What repairing `item` costs in whole euros, or `null` when there is nothing
 * to repair: not armour or a weapon, or already at its own ceiling (a
 * never-fired gun has no `durability` key and reads as full). The denominator
 * is the DEF's maximum, so twenty points cost the same on the first repair and
 * the fortieth; the numerator is the bare `baseValueEur` — on a gun the
 * receiver's, since wear rides on the weapon instance and a barrel carries no
 * durability. Rounded up and never below 1.
 */
export function repairCostEur(item: ItemInstance): number | null {
  const def = repairable(item);
  if (def === null) return null;

  const max = ceilingOf(def, item);
  const missing = max - (item.durability ?? max);
  if (missing <= 0) return null;

  const eur = baseValueEur(item.defId) * (missing / def.maxDurability) * REPAIR_COST_FRAC;
  return Math.max(1, Math.ceil(eur));
}

/** One decimal, and no float dust. What both the wire and the tooltip see. */
function round1(x: number): number {
  return Math.round(x * 10) / 10;
}

/**
 * The item as it comes off the bench: full again, permanently smaller. `null`
 * under exactly the conditions `repairCostEur` answers `null`, through the
 * same `repairable` gate. A replacement, never a mutation. The new ceiling is
 * always strictly below the def's, so the key is written unconditionally; it
 * is floored at 1 so a plate repaired into the ground is worthless but never a
 * zero denominator in `resolveArmorHit` or `wearExtraSpreadDeg`, and a rebuilt
 * gun still fires.
 */
export function applyRepair(item: ItemInstance): ItemInstance | null {
  const def = repairable(item);
  if (def === null) return null;

  const cur = ceilingOf(def, item);
  if (cur - (item.durability ?? cur) <= 0) return null;

  const newMax = Math.max(1, round1(cur - def.maxDurability * REPAIR_MAX_LOSS_FRAC));
  return { ...item, durability: newMax, maxDurability: newMax };
}
